import { Tool } from '@procureai/agents';
import { ToolError, ToolLoading, ToolResult } from '@procureai/uix/ai';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { SvbrgSupplierStructuredSearchInput, SvbrgSupplierStructuredSearchOutput } from '@/generated/gql/graphql';
import { I18nNamespace } from '@/legacy/common/types';
import { useSupplierStructuredSearchResultDataGrid } from '~/routes/IntakeChat/components/SupplierStructuredSearchResult/SupplierStructuredSearchResult.dataGrid';

import { ToolResultComponent } from '.';
import { ResultsTableCard } from './components/ResultsTableCard';

export const SupplierStructuredSearchTool: ToolResultComponent<
  Tool.supplierStructuredSearch,
  SvbrgSupplierStructuredSearchInput,
  SvbrgSupplierStructuredSearchOutput
> = ({ tool, toolName }) => {
  const { t } = useTranslation(I18nNamespace.intake, { keyPrefix: `Chat.tools.${toolName}` });

  const config = useSupplierStructuredSearchResultDataGrid();

  // Build search description for loading message
  const buildSearchDescription = useCallback(
    (input?: SvbrgSupplierStructuredSearchInput) => {
      const filters = input?.filters?.map((f) => `${f.entityField} ${f.operation} "${f.value}"`);
      return filters && filters.length > 0 ? filters.join(', ') : t('structuredCriteria');
    },
    [t],
  );

  switch (tool.state) {
    case 'input-streaming':
    case 'input-available':
      return (
        <ToolLoading
          label={t(`call`, {
            criteria: buildSearchDescription(tool.input),
          })}
        />
      );

    case 'output-error': {
      return (
        <ToolError
          label={t(`error`, {
            error: tool.errorText || t('unknownError'),
          })}
        />
      );
    }

    case 'output-available': {
      if (!tool.output.success || tool.output.error) {
        return (
          <ToolError
            label={t(`error`, {
              error: tool.output.error || t('unknownError'),
            })}
          />
        );
      }

      const hasResults = (tool.output.results || []).length > 0;
      const message = hasResults
        ? t(`result`, {
            count: tool.output?.totalResults || 0,
            criteria: buildSearchDescription(),
            executionTime: tool.output?.executionTime || 0,
          })
        : t(`noResults`, {
            criteria: buildSearchDescription(),
          });

      return (
        <ToolResult label={message} defaultOpen={false}>
          {/* NOTE: We hide this section for now to make the UI cleaner. We can re-enable it later if needed and maybe add it only to dev.
          Input parameters section
          <Box sx={{ mt: 1 }}>
            <ParameterSection t={t} filters={filters} orderBy={orderBy} limit={limit} />
          </Box>
          */}

          {hasResults && <ResultsTableCard results={tool.output.results} config={config} />}
        </ToolResult>
      );
    }
  }
};
